/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {
    useAccessToken,
    useCategory,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import axios from 'axios'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import TagManager from 'react-gtm-nonce'
import {useLocation} from 'react-router-dom'
import {useAppOrigin} from '~/app/hooks/use-app-origin'
import {useScript} from '~/app/hooks/use-script'
import {Toaster} from 'sonner'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'

// Local Project Components
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'

// Hooks
import {useCreateBasket} from '@/hooks/basket/useCreateBasket'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useUpdateShopperContext} from '@salesforce/retail-react-app/app/hooks/use-update-shopper-context'
import {AddToCartModalProvider} from '~/app/hooks/AddtoCartModal/use-add-to-cart-modal'

// HOCs
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {
    ACTIVE_DATA_ENABLED,
    DEFAULT_LOCALE,
    THEME_COLOR
} from '@salesforce/retail-react-app/app/constants'
import {fetchTranslations, getTargetLocale} from '@salesforce/retail-react-app/app/utils/locale'
import {isServer, watchOnlineStatus} from '@salesforce/retail-react-app/app/utils/utils'
import {initializeSegment} from '~/app/utils/segment/config'
import {Helmet} from 'react-helmet'
import Seo from '~/app/components/seo'
import {formatSiteName} from '~/app/utils/helpers'
import {IdentifySegmentUser} from '~/app/utils/segment/IdentifySegmentUser'
import {LearningCenterNavTracker} from '~/app/utils/segment/learning-center/LearningCenterNavTracker'
import {TpaConsentTracker} from '~/app/utils/segment/TpaConsentTracker'
import {
    BLOOMREACH_TRACKING_NONPROD_SCRIPT,
    BLOOMREACH_TRACKING_PROD_SCRIPT
} from '../../third-party-scripts'
import CookieTrackerProvider from '../../utils/cookieTracking/CookieTrackerProvider'
import {useTurnstileScript} from '../../utils/cloudflare/turnstile/useTurnstileScript'

// Styles
import {isClient} from '@/utils/common'
import {ClientSitePreferences} from 'hec/context/site-preferences'
import {ClientGlobalPreferences} from 'hec/context/global-preferences'
import {TpaConfigProvider} from 'hec/context/tpa-config'
import 'hec/css/tailwind.css'
import {getClientSitePreferences} from 'hec/helpers/customSitePreferences'
import {getClientGlobalCustomPreferences} from 'hec/helpers/customGlobalPreferences'
import {getTPAContextSSR} from 'hec/helpers/ssrTpaHelper'
import {SHOPPER_TOKEN_REFRESH_INTERVAL_MINUTES} from '../../constants'
import {AbTasty} from '/hec/components/abTasty'

if (isClient) {
    axios
        .get('/nonce')
        .then((res) => {
            const config = getConfig()
            const tagManagerArgs = {
                gtmId: config.app.gtm.id,
                nonce: res.data
            }
            TagManager.initialize(tagManagerArgs)
        })
        .catch((err) => {
            console.log(err)
        })
}

const PlaceholderComponent = () => (
    <div className="flex items-center justify-center p-2">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-current border-t-transparent" />
    </div>
)

const DrawerMenuItemWithData = withCommerceSdkReact(
    ({itemComponent: ItemComponent, data, ...rest}) => (
        <div className="animate-fadeIn">
            <ItemComponent {...rest} item={data} itemComponent={DrawerMenuItemWithData} />
        </div>
    ),
    {
        hook: useCategory,
        queryOptions: ({item}) => ({
            parameters: {
                id: item.id
            }
        }),
        placeholder: PlaceholderComponent
    }
)
// @ts-check
/**
 * @type {import('react').FC<{
 *   children: React.ReactNode;
 *   preferences: import('hec/context/site-preferences').ClientSitePrefs;
 *   tpaData: import('hec/helpers/ssrTpaHelper').TpaConfigContextType;
 *  globalCustomPreferences: import('hec/context/site-preferences').ClientSitePrefs;
 * }>}
 */
const App = (props) => {
    const {
        children,
        preferences = {
            CloudFlareConfigs: {cloudFlareTurnstileSiteKey: '', cloudFlareTurnstileEnabled: false}
        },
        tpaData,
        globalCustomPreferences
    } = props

    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const activeData = useActiveData()
    const location = useLocation()
    const {site, locale, buildUrl} = useMultiSite()
    const [isOnline, setIsOnline] = useState(true)
    const storeName = formatSiteName(site.id).toLowerCase()
    const config = getConfig()
    const bloomreachEventTarget = config?.app?.bloomreach?.eventTarget
    const isProdInstance = config?.app.sfccInstanceIdentifier === 'prd'

    const queryClient = useQueryClient()
    if (isClient) {
        window.__TANSTACK_QUERY_CLIENT__ = queryClient
    }

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer
    })

    useTurnstileScript({enabled: !isServer})

    // Used to conditionally render header/footer for checkout page

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket, isLoading: isBasketsDataLoading} = useCurrentBasket()
    // Only allow basket creation once per session
    const shouldCreateBasket =
        !!customer?.customerId &&
        basket?.basketId === undefined &&
        !basket?.baskets?.length &&
        !isBasketsDataLoading

    const {data: createdBasketData} = useCreateBasket({
        customer: customer ?? undefined,
        enabled: shouldCreateBasket
    })

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency}
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    useEffect(() => {
        if (config.app?.segment?.id) {
            initializeSegment(config.app.segment.id)
        }
    }, [config?.app?.segment?.id])

    useEffect(() => {
        const intervalId = setInterval(() => {
            void getTokenWhenReady()
                .then(() => {
                    console.log('Token refreshed')
                })
                .catch((error) => {
                    console.error('Failed to refresh token:', error)
                })
        }, SHOPPER_TOKEN_REFRESH_INTERVAL_MINUTES * 60 * 1000)

        return () => clearInterval(intervalId)
    }, [getTokenWhenReady])

    let tailwindThemeClass = ''

    switch (site.id) {
        case 'FSASTORE':
            tailwindThemeClass = 'FSASTORE'
            break
        case 'HSASTORE':
            tailwindThemeClass = 'HSASTORE'
            break
        case 'WDH':
            tailwindThemeClass = 'WDH'
            break
        default:
            throw new Error(`Unsupported theme ${site.id}`)
    }
    // Add third party scripts dynamatically e.g. Bloomreach
    const scripts = [
        {
            shouldLoad: true,
            id: 'bloomreach',
            content: isProdInstance
                ? BLOOMREACH_TRACKING_PROD_SCRIPT(bloomreachEventTarget)
                : BLOOMREACH_TRACKING_NONPROD_SCRIPT(bloomreachEventTarget),
            type: 'text/javascript'
        }
    ]
    useScript({scripts})

    return (
        <ClientGlobalPreferences preferences={globalCustomPreferences}>
            <ClientSitePreferences preferences={preferences}>
                <TpaConfigProvider
                    serverBalanceConfig={tpaData.balanceConfig}
                    serverSuppressionData={tpaData.suppressionData}
                >
                    <div className="sf-app flex flex-col flex-1 bg-white min-w-[375px]">
                        <Toaster position="bottom-center" />
                        <AbTasty />
                        <Helmet>
                            <body className={tailwindThemeClass} />
                        </Helmet>
                        <StorefrontPreview getToken={getTokenWhenReady}>
                            <Helmet>
                                {ACTIVE_DATA_ENABLED && (
                                    <script
                                        src={getAssetUrl('static/head-active_data.js')}
                                        id="headActiveData"
                                        type="text/javascript"
                                    ></script>
                                )}
                                {<link rel="stylesheet" href={getAssetUrl('main.css')} />}
                                {config.app?.zendesk?.id && (
                                    <script
                                        id="ze-snippet"
                                        type="text/javascript"
                                        src={`https://static.zdassets.com/ekr/snippet.js?key=${config.app.zendesk.id}`}
                                    ></script>
                                )}
                                <meta
                                    name="viewport"
                                    content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
                                />
                            </Helmet>
                            <IntlProvider
                                onError={(err) => {
                                    if (!messages) {
                                        // During the ssr prepass phase the messages object has not loaded, so we can suppress
                                        // errors during this time.
                                        return
                                    }
                                    if (err.code === 'MISSING_TRANSLATION') {
                                        // NOTE: Remove the console error for missing translations during development,
                                        // as we knew translations would be added later.
                                        logger.warn('Missing translation', {
                                            namespace: 'App.IntlProvider',
                                            additionalProperties: {
                                                errorMessage: err.message
                                            }
                                        })
                                        return
                                    }
                                    throw err
                                }}
                                locale={targetLocale}
                                messages={messages}
                                // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                                // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                                // - "extract-default-translations"
                                // - "compile-translations:pseudo"
                                defaultLocale={DEFAULT_LOCALE}
                            >
                                <CurrencyProvider currency={currency}>
                                    <Seo>
                                        <meta name="theme-color" content={THEME_COLOR} />
                                        <meta
                                            name="apple-mobile-web-app-title"
                                            content={`${config.app.threeLetter} Store`}
                                        />
                                        <link
                                            rel="icon"
                                            type="image/x-icon"
                                            href={getAssetUrl(
                                                `static/app_${storeName}/ico/favicon.ico`
                                            )}
                                        />
                                        <link
                                            rel="apple-touch-icon"
                                            type="image/png"
                                            href={getAssetUrl(
                                                `static/app_${storeName}/img/global/apple-touch-icon.png`
                                            )}
                                        />
                                        <link
                                            rel="manifest"
                                            href={getAssetUrl('static/manifest.json')}
                                        />

                                        {/* Urls for all localized versions of this page (including current page)
                        For more details on hrefLang, see https://developers.google.com/search/docs/advanced/crawling/localized-versions */}
                                        {site.l10n?.supportedLocales.map((locale) => (
                                            <link
                                                rel="alternate"
                                                hrefLang={locale.id.toLowerCase()}
                                                href={`${appOrigin}${buildUrl(location.pathname)}`}
                                                key={locale.id}
                                            />
                                        ))}
                                        {/* A general locale as fallback. For example: "en" if default locale is "en-GB" */}
                                        <link
                                            rel="alternate"
                                            hrefLang={site.l10n.defaultLocale.slice(0, 2)}
                                            href={`${appOrigin}${buildUrl(location.pathname)}`}
                                        />
                                        {/* A wider fallback for user locales that the app does not support */}
                                        <link
                                            rel="alternate"
                                            hrefLang="x-default"
                                            href={`${appOrigin}/`}
                                        />
                                    </Seo>

                                    <CookieTrackerProvider>
                                        <ScrollToTop />
                                        <IdentifySegmentUser />
                                        <LearningCenterNavTracker />
                                        <TpaConsentTracker />
                                        <div id="app" className="flex flex-col flex-1">
                                            <a
                                                href="#app-main"
                                                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:shadow-lg"
                                            >
                                                Skip to Content
                                            </a>
                                            {!isOnline && <OfflineBanner />}
                                            <AddToCartModalProvider>
                                                <div className="flex flex-col flex-1 outline-none">
                                                    <main
                                                        id="app-main"
                                                        role="main"
                                                        className="flex flex-col flex-1"
                                                    >
                                                        <OfflineBoundary isOnline={false}>
                                                            {children}
                                                        </OfflineBoundary>
                                                    </main>
                                                </div>
                                            </AddToCartModalProvider>
                                        </div>
                                    </CookieTrackerProvider>
                                </CurrencyProvider>
                            </IntlProvider>
                            {ACTIVE_DATA_ENABLED && (
                                <script
                                    type="text/javascript"
                                    src={getAssetUrl('static/dwanalytics-22.2.js')}
                                    id="dwanalytics"
                                    async="async"
                                    onLoad={trackPage}
                                ></script>
                            )}
                            {ACTIVE_DATA_ENABLED && (
                                <script
                                    src={getAssetUrl('static/dwac-21.7.js')}
                                    type="text/javascript"
                                    id="dwac"
                                    async="async"
                                ></script>
                            )}
                        </StorefrontPreview>
                    </div>
                </TpaConfigProvider>
            </ClientSitePreferences>
        </ClientGlobalPreferences>
    )
}
App.shouldGetProps = () => isServer
App.getProps = async ({params, req}) => {
    // micro-apps MRT only serves API routes & static bundles —
    // skip preferences on SSR fallthrough (404s, bots, healthchecks)
    const config = getConfig()
    if (config.app?.environmentKey?.startsWith('micro-apps')) {
        return {preferences: {}, tpaData: {}, globalCustomPreferences: {}}
    }

    const preferences = await getClientSitePreferences(req)
    const tpaData = getTPAContextSSR(req, preferences)
    const globalCustomPreferences = await getClientGlobalCustomPreferences(req)

    return {
        preferences,
        tpaData,
        globalCustomPreferences
    }
}
App.propTypes = {
    children: PropTypes.node,
    /** @type {import('hec/context/site-preferences').ClientSitePrefs} */
    preferences: PropTypes.object,
    /** @type {import('hec/helpers/ssrTpaHelper').TpaConfigContextType} */
    tpaData: PropTypes.object,
    /** @type {import('hec/context/site-preferences').ClientSitePrefs} */
    globalCustomPreferences: PropTypes.object
}

export default App
